import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Timestamp,
  uuid,
  type Trip,
  type TripIdea,
} from "@travelix/core";
import { CalendarPlus, Lightbulb, X } from "lucide-react";

import { useAuth } from "@/lib/auth-provider";
import { EmptyState } from "@/components/empty-state";
import { Avatar } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/ui/field";
import { Sheet, SheetContent, SheetHeader, SheetBody, SheetFooter } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { addTripIdea, removeTripIdea, unwrap, queryKeys, displayNameOf } from "@/lib/api";
import { useTripMutation } from "@/hooks/use-trip-mutation";

/**
 * Ideas — a wishlist per trip. Anyone can add ("night out", "hike",
 * "eat paella"); anyone can promote an idea into a scheduled activity
 * (opens Add-event pre-filled).
 *
 * Add flow lives in a bottom sheet, opened by the parent PlanTab's
 * bottom action bar — matches the Timeline/Expenses add pattern so
 * users don't have to hunt for a second affordance at the top.
 */
export function IdeasSection({
  trip,
  adding,
  onCloseAdd,
  readOnly = false,
}: {
  trip: Trip;
  adding: boolean;
  onCloseAdd: () => void;
  /** Past trips: no adds, no schedule, no removes. */
  readOnly?: boolean;
}) {
  const { user } = useAuth();
  const navigate = useNavigate();

  const ideas = trip.ideas ?? [];
  const unscheduled = ideas.filter((i) => !i.scheduledActivityId);
  const scheduled = ideas.filter((i) => !!i.scheduledActivityId);

  const remove = useTripMutation({
    keys: [queryKeys.trip(trip.id)],
    mutationFn: (id: string) => unwrap(removeTripIdea(trip.id, id)),
    errorToast: "Couldn't remove.",
  });

  // One-tap remove sat right next to other row actions — confirm first
  // so a stray tap doesn't drop something the group was counting on.
  const [pendingRemove, setPendingRemove] = useState<TripIdea | null>(null);

  const schedule = (idea: TripIdea) => {
    // Pre-populate Add-event with the idea's text and its id, so on success
    // the route can mark the idea scheduled. Kept in URL state.
    const params = new URLSearchParams({ name: idea.text, ideaId: idea.id });
    navigate(`/trip/${trip.id}/add-event?${params.toString()}`);
  };

  return (
    <>
      {unscheduled.length === 0 && scheduled.length === 0 ? (
        <EmptyState
          icon={Lightbulb}
          title={readOnly ? "No ideas were logged" : "What do you want to do?"}
          body={
            readOnly
              ? "This trip is in the past — nothing more can be added."
              : "Drop anything you'd love to see or eat here — everyone can contribute. Promote an idea to the timeline when you're ready to commit."
          }
        />
      ) : null}

      {unscheduled.length > 0 ? (
        <section>
          <p className="mb-2 px-1 text-[11px] font-bold uppercase tracking-widest text-ink-subtle">
            Ideas · {unscheduled.length}
          </p>
          <ul className="flex flex-col divide-y divide-line rounded-xl border border-line bg-bg-elevated">
            {unscheduled.map((i) => (
              <IdeaRow
                key={i.id}
                idea={i}
                onSchedule={readOnly ? undefined : () => schedule(i)}
                onRemove={readOnly ? undefined : () => setPendingRemove(i)}
                canRemove={!readOnly && i.addedBy === user?.uid}
                busy={remove.isPending}
              />
            ))}
          </ul>
        </section>
      ) : null}

      {scheduled.length > 0 ? (
        <section>
          <p className="mb-2 px-1 text-[11px] font-bold uppercase tracking-widest text-ink-subtle">
            Already scheduled · {scheduled.length}
          </p>
          <ul className="flex flex-col divide-y divide-line rounded-xl border border-line bg-bg-elevated/50 opacity-70">
            {scheduled.map((i) => (
              <IdeaRow key={i.id} idea={i} scheduled />
            ))}
          </ul>
        </section>
      ) : null}

      {adding && !readOnly ? <AddIdeaSheet trip={trip} onClose={onCloseAdd} /> : null}

      <ConfirmDialog
        open={!!pendingRemove}
        onOpenChange={(v) => !v && setPendingRemove(null)}
        title={`Remove "${pendingRemove?.text ?? ""}"?`}
        description="It disappears from the ideas list for everyone."
        confirmLabel="Remove"
        destructive
        onConfirm={async () => {
          if (pendingRemove) await remove.mutateAsync(pendingRemove.id);
          setPendingRemove(null);
        }}
      />
    </>
  );
}

// ─── Add-idea sheet ─────────────────────────────────────────
function AddIdeaSheet({ trip, onClose }: { trip: Trip; onClose: () => void }) {
  const { user } = useAuth();
  const [text, setText] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const add = useTripMutation({
    keys: [queryKeys.trip(trip.id)],
    mutationFn: async () => {
      const t = text.trim();
      if (!t) throw new Error("Add a few words.");
      if (!user?.uid) throw new Error("Not signed in.");
      const idea: TripIdea = {
        id: uuid(),
        text: t,
        addedBy: user.uid,
        addedByName: displayNameOf(user),
        addedAt: Timestamp.now(),
      };
      await unwrap(addTripIdea(trip.id, idea));
    },
    successToast: "Added to Ideas.",
    onError: (e) => setErr(e instanceof Error ? e.message : "Couldn't add."),
    onSuccess: onClose,
  });

  return (
    <Sheet open guardUnsaved onOpenChange={(o) => !o && !add.isPending && onClose()}>
      <SheetContent>
        <SheetHeader
          title="New idea"
          description="Anything you'd love the group to consider — a place, a meal, a whole day."
        />
        <form
          onSubmit={(e) => {
            e.preventDefault();
            add.mutate();
          }}
        >
          <SheetBody>
            <Field id="new-idea" label="What is it?" error={err ?? undefined}>
              <Input
                id="new-idea"
                type="text"
                autoFocus
                value={text}
                onChange={(e) => {
                  setText(e.target.value);
                  if (err) setErr(null);
                }}
                placeholder="Eat paella, night out, Sagrada Familia…"
                maxLength={120}
              />
            </Field>
          </SheetBody>
          <SheetFooter>
            <Button type="submit" block disabled={add.isPending || !text.trim()}>
              {add.isPending ? "Adding…" : "Add to Ideas"}
            </Button>
            <Button type="button" variant="ghost" block onClick={onClose} disabled={add.isPending}>
              Cancel
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}

// ─── Row ────────────────────────────────────────────────────
function IdeaRow({
  idea,
  onSchedule,
  onRemove,
  canRemove,
  scheduled,
  busy,
}: {
  idea: TripIdea;
  onSchedule?: () => void;
  onRemove?: () => void;
  canRemove?: boolean;
  scheduled?: boolean;
  busy?: boolean;
}) {
  return (
    <li className="flex items-center gap-3 px-4 py-3">
      <Avatar name={idea.addedByName} colorSeed={hashSeed(idea.addedBy)} size="sm" />
      <div className="flex-1 min-w-0">
        <p
          className={cn(
            "text-[15px] font-medium text-ink m-0 truncate",
            scheduled && "line-through",
          )}
        >
          {idea.text}
        </p>
        <p className="text-[12px] text-ink-subtle m-0 truncate">
          {idea.addedByName}
        </p>
      </div>
      {scheduled ? (
        <span className="rounded-full bg-success-soft px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-success">
          Scheduled
        </span>
      ) : (
        <>
          {onSchedule ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onSchedule}
              className="text-primary hover:bg-primary-soft"
              aria-label="Schedule this idea"
            >
              <CalendarPlus className="h-4 w-4" />
              Schedule
            </Button>
          ) : null}
          {canRemove && onRemove ? (
            <button
              type="button"
              onClick={onRemove}
              disabled={busy}
              aria-label="Remove idea"
              className="flex h-8 w-8 items-center justify-center rounded-full text-ink-subtle hover:bg-danger-soft/40 hover:text-danger disabled:opacity-50"
            >
              <X className="h-4 w-4" />
            </button>
          ) : null}
        </>
      )}
    </li>
  );
}

function hashSeed(uid: string): number {
  let h = 0;
  for (let i = 0; i < uid.length; i++) h = (h * 31 + uid.charCodeAt(i)) | 0;
  return Math.abs(h);
}
